import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ComponentPropsWithoutRef } from 'react';

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface QuizProps {
  questions: QuizQuestion[];
}

export default function Quiz({ questions }: QuizProps) {
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: string]: number }>({});
  const [showExplanations, setShowExplanations] = useState<{ [key: string]: boolean }>({});
  const [submitted, setSubmitted] = useState(false);

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    if (!submitted) {
      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: answerIndex
      }));
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowExplanations({});
    setSubmitted(false);
  };

  const toggleExplanation = (questionId: string) => {
    setShowExplanations(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getScore = () => {
    return questions.reduce((score, question) => {
      return score + (selectedAnswers[question.id] === question.correctAnswer ? 1 : 0);
    }, 0);
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="space-y-8">
        {questions.map((question, index) => (
          <div
            key={question.id}
            className="bg-[#1a1a1a] rounded-lg p-6 shadow-lg"
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white mb-2">
                Question {index + 1}
              </h3>
              <div className="prose prose-invert max-w-none">
                <ReactMarkdown
                  components={{
                    code({ inline, className, children, ...props }: ComponentPropsWithoutRef<'code'> & { inline?: boolean }) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline ? (
                        <div className="relative">
                          <pre className="!bg-[#0f0f0f] !p-4 !rounded-lg !mt-2 !mb-4 overflow-x-auto">
                            <code className={`${match ? `language-${match[1]}` : ''} !font-mono !text-sm`} {...props}>
                              {String(children).replace(/\n$/, '')}
                            </code>
                          </pre>
                        </div>
                      ) : (
                        <code className="!bg-[#0f0f0f] !px-2 !py-1 !rounded !text-sm !font-mono !text-blue-300" {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {question.question}
                </ReactMarkdown>
              </div>
            </div>
            <div className="space-y-3">
              {question.options.map((option, optionIndex) => (
                <button
                  key={optionIndex}
                  onClick={() => handleAnswerSelect(question.id, optionIndex)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedAnswers[question.id] === optionIndex
                      ? submitted
                        ? optionIndex === question.correctAnswer
                          ? 'bg-green-600/20 border-2 border-green-500'
                          : 'bg-red-600/20 border-2 border-red-500'
                        : 'bg-blue-600/20 border-2 border-blue-500'
                      : submitted && optionIndex === question.correctAnswer
                      ? 'bg-green-600/20 border-2 border-green-500'
                      : 'bg-[#0f0f0f] hover:bg-[#2a2a2a] border-2 border-transparent'
                  }`}
                  disabled={submitted}
                >
                  <div className="flex items-start">
                    <div className="shrink-0 w-6">
                      {String.fromCharCode(65 + optionIndex)}.
                    </div>
                    <div className="ml-2">{option}</div>
                  </div>
                </button>
              ))}
            </div>
            {submitted && (
              <div className="mt-4">
                <button
                  onClick={() => toggleExplanation(question.id)}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                >
                  {showExplanations[question.id] ? 'Hide' : 'Show'} Explanation
                </button>
                {showExplanations[question.id] && (
                  <div className="mt-2 p-4 bg-[#0f0f0f] rounded-lg text-white/80 text-sm">
                    {question.explanation}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        {!submitted ? (
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            disabled={questions.length !== Object.keys(selectedAnswers).length}
          >
            Submit
          </button>
        ) : (
          <>
            <div className="text-lg font-semibold text-white">
              Score: {getScore()} / {questions.length}
            </div>
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
